/**
 * DjayAvatar — shared types, size scale and SVG geometry constants.
 *
 * Extracted from the former `DjayAvatar.tsx` god-file (~1619 r.) into a
 * scalable `mascot/avatar/` module (MASCOT_SSOT_ARCHITECTURE_AUDIT_2026 §4.1).
 * This is the canonical home for the avatar's public type contract — adding a
 * new variant/accessory/emotion happens here first, then the matching render
 * branch in `parts/`.
 */
export type DjaySize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'hero';
export type DjayEmotion = 'happy' | 'excited' | 'encouraging' | 'celebrating' | 'thinking' | 'sleeping' | 'pointing' | 'surprised' | 'proud';
export type DjayTier = 'rookie' | 'pro' | 'master';
export type DjayVariant = 'default' | 'blaze' | 'frost' | 'pixel' | 'royal' | 'cosmic' | 'shadow' | 'goud' | 'rainbow';

/**
 * Treatment finishes — a cosmetic axis ORTHOGONAL to identity skins
 * (variant) and wearable accessories. A treatment is a "rarity finish" that
 * applies on top of ANY variant without changing identity semantics
 * (Fortnite.GG-style locker model, DJAY_MASCOT_DOMAIN_2026 §Treatments).
 *
 *   none    — base render, no finish
 *   glow    — soft inner glow + subtly glowing eyes (uncommon)
 *   gummy   — translucent "gummy" body with deep inner light (rare)
 *   sparkle — drifting sparkle particles above the head (rare)
 *   aura    — pulsing radial aura halo + glowing eyes (legendary)
 */
export type DjayTreatment = 'none' | 'glow' | 'gummy' | 'sparkle' | 'aura';
export type DjayAccessory = 'none' | 'seedling' | 'pencil' | 'badge100' | 'smallFlame' | 'trophy' | 'doubleFlameCrown' | 'starWand' | 'tripleStars' | 'scepter' | 'leafGarland' | 'medal' | 'cosmicOrb' | 'shadowOrb' | 'sunglasses' | 'cap' | 'goldChain';
export interface DjayAvatarProps {
  size?: DjaySize;
  emotion?: DjayEmotion;
  tier?: DjayTier;
  variant?: DjayVariant;
  accessory?: DjayAccessory;
  /** Rarity finish applied over the variant (aura/sparkle/glow/gummy). */
  treatment?: DjayTreatment;
  className?: string;
  animate?: boolean;
  hideBody?: boolean;
}

/** Per-variant colour scheme driving body/flame/arms/tail/feet/mouth. */
export interface VariantColorScheme {
  bodyGradient: [string, string, string];
  flameColors: [string, string, string, string];
  glassesFrame: string;
  glassesLens: string;
  armColor: string;
  armHighlight: string;
  tailStroke: string;
  tailHighlight: string;
  feetDark: string;
  feetLight: string;
  mouthColor: string;
  /**
   * Accent colour for treatment finishes (aura halo, glow, sparkle bits,
   * gummy inner light). Picked to read brightly against the body gradient.
   */
  treatmentAccent: string;
}

/** Rendered pixel size per named size token. */
export const SIZES: Record<DjaySize, number> = {
  xs: 16,
  sm: 24,
  md: 32,
  lg: 48,
  xl: 64,
  hero: 128
};

/** Canonical SVG viewBox all part-renderers draw against. */
export const DJAY_VIEWBOX = '0 0 200 220';

/**
 * Shared size thresholds — the single source of truth for which detail
 * layers are visible at a given pixel size. Used by the orchestrator to
 * derive the boolean flags passed into the part-renderers.
 */
export const DETAIL_THRESHOLD = 32;
export const ARM_STUB_MIN = 24;
export const MOUTH_MIN = 24;

/**
 * Static locker metadata per treatment finish. Mirrors the variant rarity
 * vocabulary so the collection grid can frame skins + finishes identically.
 * This is the single source of truth for treatment display + rarity.
 */
export const TREATMENT_META: Record<DjayTreatment, {
  name: string;
  rarity: 'common' | 'uncommon' | 'rare' | 'legendary';
  description: string;
}> = {
  none: {
    name: 'Geen finish',
    rarity: 'common',
    description: 'De pure look van Djay.'
  },
  glow: {
    name: 'Glow',
    rarity: 'uncommon',
    description: 'Een zachte gloed van binnenuit en lichtgevende ogen.'
  },
  gummy: {
    name: 'Gummy',
    rarity: 'rare',
    description: 'Een doorschijnend, snoepachtig lichaam met diep binnenlicht.'
  },
  sparkle: {
    name: 'Sparkle',
    rarity: 'rare',
    description: 'Glinsterende deeltjes die boven Djay zweven.'
  },
  aura: {
    name: 'Aura',
    rarity: 'legendary',
    description: 'Een pulserende energie-aura die Djay omhult.'
  }
};